/**
 * Google Analytics 4 — prod-only page_view tracking (quick-260727-n07), extended to
 * the Capacitor iOS/Android shells in quick-261021-rcw.
 *
 * Gate: `import.meta.env.PROD && VITE_GA_MEASUREMENT_ID` (mirrors the Sentry gate
 * in main.tsx). Nothing above the gate touches `document`, `window.dataLayer`, or
 * the network, so dev builds and sandbox deploys (which never set the var) are a
 * true no-op — Vite folds the condition to a constant and dead-code-eliminates
 * the rest. The native shells run this same bundle, but the committed
 * `.env.capacitor.prod` deliberately leaves the ID unset: only the prod RELEASE
 * workflows (ios-release-prod.yml / android-release-prod.yml) inject it, so
 * hand-built test installs never report into the prod property.
 *
 * Deliberate decision: `gtag('config', ..., { send_page_view: false })` disables
 * gtag.js's own automatic initial page_view. Every page_view — including the
 * first one — is sent by `sendPageView()` below, from one code path, so the
 * initial load can never be double-counted against the first router navigation.
 * A `lastKey` dedupe additionally guards against firing twice for the same
 * location regardless of whether TanStack fires `onResolved` for the initial
 * route as well.
 *
 * Native shells (quick-261021-rcw): the WebView origin is capacitor://localhost (iOS)
 * or https://localhost (Android), where GA's `_ga` cookie is unreliable — every launch
 * could count as a new user. So on native we pass an explicit, randomly generated
 * `client_id` persisted in localStorage (the documented gtag config field; the
 * undocumented `client_storage: 'none'` is not relied on). page_location is rewritten
 * to https://app.brokeornot.com + the route so reports show real paths rather than
 * capacitor://localhost — set globally (`gtag('set', …)`) before each page_view, so
 * auto-collected hits carry it too — and Google signals / ad personalisation are off.
 * GA stream setting to keep off: enhanced measurement "page changes based on browser
 * history events", which would send its own localhost page_views alongside ours.
 * Every hit carries `app_platform` (ios | android | web) as a user property and event
 * param — not `platform`, which would shadow GA's built-in dimension. No user_id or
 * Cognito sub is ever sent, so the data stays "not linked" to the user.
 */
import type { AnyRouter } from '@tanstack/react-router';
import { Capacitor } from '@capacitor/core';
import { isRunningInNativeShell } from '@/lib/pwa';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export type AnalyticsPlatform = 'ios' | 'android' | 'web';

/** localStorage key of the persisted native GA client_id. */
export const GA_CLIENT_ID_STORAGE_KEY = 'brokeornot.ga.cid.v1';

/** The public web origin native page_locations are reported under. */
export const NATIVE_PAGE_ORIGIN = 'https://app.brokeornot.com';

const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

type MinimalStorage = Pick<Storage, 'getItem' | 'setItem'>;

function defaultRandUint31(): number {
  try {
    return globalThis.crypto.getRandomValues(new Uint32Array(1))[0] >>> 1;
  } catch {
    return Math.floor(Math.random() * 0x7fffffff);
  }
}

/**
 * The device's GA client_id (`<random>.<unix seconds>`, the gtag cid format), created
 * and persisted on first use. A throwing/missing storage still yields a usable id
 * (just not a persisted one).
 */
export function getOrCreateClientId(
  storage: MinimalStorage | undefined,
  nowMs: number,
  randUint31: () => number = defaultRandUint31,
): string {
  try {
    const existing = storage?.getItem(GA_CLIENT_ID_STORAGE_KEY);
    if (existing) return existing;
  } catch {
    // fall through and mint one
  }
  const cid = `${randUint31()}.${Math.floor(nowMs / 1000)}`;
  try {
    storage?.setItem(GA_CLIENT_ID_STORAGE_KEY, cid);
  } catch {
    // not persisted — still usable for this launch
  }
  return cid;
}

/** page_location for a page_view: the public URL on native, unchanged on web. */
export function pageLocationFor(
  platform: AnalyticsPlatform,
  routeHref: string,
  windowHref: string,
): string {
  return platform === 'web' ? windowHref : NATIVE_PAGE_ORIGIN + routeHref;
}

/** The `gtag('config', id, …)` params for this platform. */
export function buildConfigParams(
  platform: AnalyticsPlatform,
  clientId: string | undefined,
  debug = false,
): Record<string, unknown> {
  const params: Record<string, unknown> = {
    send_page_view: false,
    user_properties: { app_platform: platform },
  };
  if (platform !== 'web') {
    params.client_id = clientId;
    params.allow_google_signals = false;
    params.allow_ad_personalization_signals = false;
  }
  if (debug) params.debug_mode = true;
  return params;
}

function detectPlatform(): AnalyticsPlatform {
  if (!isRunningInNativeShell()) return 'web';
  const p = Capacitor.getPlatform();
  return p === 'ios' || p === 'android' ? p : 'web';
}

export function registerAnalytics(router: AnyRouter): void {
  if (!import.meta.env.PROD || !measurementId) return;
  startAnalytics(router, measurementId, detectPlatform());
}

export interface AnalyticsDeps {
  storage?: MinimalStorage;
  now?: () => number;
  randUint31?: () => number;
  /** Adds debug_mode and logs every gtag command as `[ga] …` (VITE_GA_DEBUG=true). */
  debug?: boolean;
}

/** Loads gtag.js and starts page_view tracking. Called only past the prod gate. */
export function startAnalytics(
  router: Pick<AnyRouter, 'state' | 'subscribe'>,
  id: string,
  platform: AnalyticsPlatform,
  deps: AnalyticsDeps = {},
): void {
  const debug = deps.debug ?? import.meta.env.VITE_GA_DEBUG === 'true';

  window.dataLayer = window.dataLayer ?? [];
  // Classic gtag.js shape: push the real `arguments` object (not a rest-args
  // array) onto dataLayer — gtag.js's own processor expects that shape.
  function gtag(..._args: unknown[]) {
    if (debug) console.info('[ga]', JSON.stringify(Array.from(arguments)));
    window.dataLayer!.push(arguments);
  }
  window.gtag = gtag as Window['gtag'];

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id);
  document.head.appendChild(script);

  let clientId: string | undefined;
  if (platform !== 'web') {
    let storage = deps.storage;
    if (!storage) {
      try {
        storage = window.localStorage;
      } catch {
        storage = undefined;
      }
    }
    clientId = getOrCreateClientId(storage, (deps.now ?? Date.now)(), deps.randUint31);
  }

  gtag('js', new Date());
  gtag('config', id, buildConfigParams(platform, clientId, debug));

  let lastKey: string | null = null;
  function sendPageView() {
    const key = router.state.location.href;
    if (key === lastKey) return;
    lastKey = key;
    const pageLocation = pageLocationFor(platform, router.state.location.href, window.location.href);
    // Native (review WR-06): gtag fills every OTHER hit (user_engagement, scroll, …) from
    // document.location, i.e. capacitor://localhost or https://localhost. Setting it
    // globally first makes those carry the public URL too. Web is unchanged.
    if (platform !== 'web') window.gtag?.('set', { page_location: pageLocation });
    window.gtag?.('event', 'page_view', {
      page_path: router.state.location.pathname,
      page_location: pageLocation,
      page_title: document.title,
      app_platform: platform,
    });
  }

  sendPageView();
  router.subscribe('onResolved', () => {
    sendPageView();
  });
}
